import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Table, Col, Row, Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Swal from 'sweetalert2';
import { formatPrice, roundToNearestInteger } from '../../helpers/utility';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';

const InvoiceForm = ({ initialValues, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [invoice, setInvoice] = useState(initialValues);
    const { invoiceNumber, loading } = useSelector(state => state.invoiceSupplier);


    useEffect(() => {
        if (!invoiceNumber) {
            dispatch(invoiceSupplierActions.fetchNewInvoiceNumber());
        }
    }, [])

    useEffect(() => {
        setInvoice({ ...invoice, numero: invoiceNumber })
    }, [invoiceNumber])

    const handleSubmit = (e) => {
        e.preventDefault();

        Swal.fire({
            title: t('purchase.confirmation'),
            text: t('purchase.saveOrConfirmText'),
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('common.saveAndConfirm'),
            denyButtonText: t('common.save'),
            cancelButtonText: t('common.cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    invoice: {
                        ...invoice,
                        statut: 'IMPAYE',
                        referenceBons: invoice.receiptMappings.map(receipt => receipt.numero).join(','),
                    },

                    onSuccess: (invoice) => {
                        Swal.fire({
                            icon: 'success',
                            title: t('facture.invoiceSaved'),
                            text: t('facture.invoiceSavedMessage', { invoiceNumber: invoice.numero }),
                        });
                        onClose();
                    },

                    onError: (error) => {
                        Swal.fire({
                            icon: 'error',
                            title: t('facture.invoiceSaveError'),
                            text: t('facture.invoiceSaveErrorMessage', { error }),
                        });

                    },
                };

                if (invoice.id) {
                    dispatch(invoiceSupplierActions.updateInvoice(payload));
                } else {
                    dispatch(invoiceSupplierActions.createInvoice(payload));
                }
            } else if (result.isDenied) {
                let payload = {
                    invoice: {
                        ...invoice,
                        statut: 'IMPAYE',
                        referenceBons: invoice.receiptMappings.map(receipt => receipt.numero).join(','),
                    },

                    onSuccess: (invoice) => {
                        Swal.fire({
                            icon: 'success',
                            title: t('facture.invoiceSaved'),
                            text: t('facture.invoiceSavedMessage', { invoiceNumber: invoice.numero }),
                        });
                        onClose();
                    },

                    onError: (error) => {
                        Swal.fire({
                            icon: 'error',
                            title: t('facture.invoiceSaveError'),
                            text: t('facture.invoiceSaveErrorMessage', { error }),
                        });

                    },
                };

                if (invoice.id) {
                    dispatch(invoiceSupplierActions.updateInvoice(payload));
                } else {
                    dispatch(invoiceSupplierActions.createInvoice(payload));
                }
            }
        });



    };

    return (
        <Form onSubmit={handleSubmit}>
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                </div>
            )}
            <Card>
                <CardBody>
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.invoiceNumber')}</Label>
                                <Input type="text" value={invoice.numero || ''} disabled />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.supplier')}</Label>
                                <Input type="text" value={invoice.fournisseurNom} disabled />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.numeroFactfournisseur')}</Label>
                                <Input
                                    type="text"
                                    name="numeroFactfournisseur"
                                    value={invoice.numeroFactfournisseur || ''}
                                    onChange={(e) => setInvoice({ ...invoice, numeroFactfournisseur: e.target.value })}
                                    disabled={invoice.statut !== 'IMPAYE'}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>{t('facture.dateFacture')}</Label>
                                <Input
                                    type="date"
                                    name="dateFacture"
                                    value={invoice.dateFacture || ''}
                                    onChange={(e) => setInvoice({ ...invoice, dateFacture: e.target.value })}
                                    disabled={invoice.statut !== 'IMPAYE'}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <Label htmlFor="dateOperation">{t('commande.dateOperation')}:</Label>
                            <Input
                                type="date"
                                value={invoice.dateOperation || ''}
                                onChange={(e) => setInvoice({ ...invoice, dateOperation: e.target.value })}
                                className="form-control"
                                disabled={invoice.statut !== 'IMPAYE'}
                            />
                        </Col>
                    </Row>

                    <Card className="mt-1 mb-1">
                        <CardHeader>
                            <h5 className="mb-0">{t('reception.receiptNotes')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('reception.receiptNumber')}</th>
                                        <th>{t('reception.receiptDate')}</th>
                                        <th>{t('purchase.montantTTC')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.receiptMappings && invoice.receiptMappings.map((receipt, index) => (
                                        <tr key={index}>
                                            <td>{receipt.numero}</td>
                                            <td>{new Date(receipt.dateReception).toLocaleDateString()}</td>
                                            <td>{formatPrice(receipt.montantTTC, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    <Card className="mb-3">
                        <CardHeader>
                            <h5 className="mb-0">{t('facture.invoiceLines')}</h5>
                        </CardHeader>
                        <CardBody>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('purchase.product')}</th>
                                        <th>{t('facture.quantity')}</th>
                                        <th>{t('devis.unite')}</th>
                                        <th>{t('purchase.unitPriceTTC')}</th>
                                        <th>{t('purchase.unitPriceHT')}</th>
                                        <th>{t('purchase.montantHT')}</th>
                                        <th>{t('purchase.tauxTVA')}</th>
                                        <th>{t('purchase.montantTTC')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.lignes && invoice.lignes.map((line, index) => (
                                        <tr key={index}>
                                            <td>{line.articleNom}</td>
                                            <td>{line.quantite}</td>
                                            <td>{line.unite}</td>
                                            <td>{roundToNearestInteger(line.prixUnitaireTTC)}</td>
                                            <td>{roundToNearestInteger(line.prixUnitaireHT)}</td>
                                            <td>{formatPrice(line.montantHT, false)}</td>
                                            <td>{line.tauxTVA * 100} %</td>
                                            <td>{formatPrice(line.montantTTC, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.paymentMethod')}</Label>
                                <select
                                    id="modeReglement"
                                    className="form-control"
                                    value={invoice.modeReglement || 'ESPECES'}
                                    onChange={(e) => setInvoice({ ...invoice, modeReglement: e.target.value })}
                                >
                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                </select>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>{t('facture.comment')}</Label>
                                <Input type="textarea" name="remarques" value={invoice.remarques} onChange={(e) => setInvoice({ ...invoice, remarques: e.target.value })} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-3 justify-content-end">
                        <Col md={4}>
                            <div className="totals text-center">
                                <p><strong>{t('facture.totalHt')}:</strong> {formatPrice(invoice.montantHT)}</p>
                                <p><strong>{t('facture.totalTVA')}:</strong> {formatPrice(invoice.montantTVA)}</p>
                                <p><strong>{t('facture.totalTtc')}:</strong> {formatPrice(invoice.montantTTC)}</p>
                            </div>
                        </Col>
                    </Row>


                    <Button color="primary" type="submit" className="me-2">{t('common.save')}</Button>
                    <Button color="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                </CardBody>
            </Card>
        </Form>
    );
};

export default InvoiceForm;